import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/joy';
import { useNavigate } from 'react-router-dom';
import Card from 'components/core/Card';
import Stack from 'components/core/Stack';
import Typography from 'components/core/Typography';
import Grid from 'components/core/Grid';
import Sheet from 'components/core/Sheet';
import Breadcrumbs from '@mui/joy/Breadcrumbs';
import IconButton from 'components/core/IconButton';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';

import InfoLine from './InfoLine';
import DossierActionButtons from './DossierActionButtons';
import DossierPrintButton from './DossierPrintButton';
import CommonUtils from 'utils/CommonUtils';

import Location from 'domain/Location';

const LocationDossier = ({ location, notes, firstObservation, lastObservation }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const dossierRef = useRef();

    if (!location) return null;

    const coordinates = location.get('coordinates');
    const latitude = coordinates?.latitude || 'Unknown';
    const longitude = coordinates?.longitude || 'Unknown';

    const dossierSchema = [
        {
            id: 'coordinates',
            label: 'Coordinates (Lat, Lng)',
            value: `${latitude}, ${longitude}`
        },
        {
            id: 'parent',
            label: 'Parent Location',
            value: location.get('parent')
        }
    ];

    const getParents = (loc, index) => {
        if (!loc) return [];
        const locData = {
            name: loc.get('label'),
            id: loc.id
        }
        if (!loc.get('parent')) return [locData];
        return [...getParents(loc.get('parent'), index + 1), locData];
    }

    return (
        <Sheet
            ref={dossierRef} 
            sx={{
                width: '100%',
                background: 'none',
                mt: 2,
                mb: 2
            }}
        >
            <Card>
                <Grid container>
                    <Grid item xs={12}>
                        <Stack direction='row' spacing={6} alignItems='end' justifyContent='space-between'>
                            <Stack direction='row' spacing={2} alignItems='end'>
                                <Typography variant='h3'>
                                    LOCATION {location.get('label')}
                                </Typography>
                            </Stack>
                            <Stack direction='row' spacing={1} className="dossier-action-buttons">
                                <DossierPrintButton 
                                    dossierRef={dossierRef} 
                                    title={`Location - ${location.get('label')}`} 
                                />
                                <DossierActionButtons className="Location" id={location.id} />
                            </Stack>
                        </Stack>
                    </Grid>
                    
                    {location.get('parent') && (
                        <Grid item xs={12}>
                            <Stack sx={{mb: 2}} direction='row' spacing={2}>
                                <Breadcrumbs>
                                    {getParents(location.get('parent'), 0).map((parent, index) => (
                                        <Typography key={index}>
                                            {parent.name}
                                        </Typography>
                                    ))}
                                </Breadcrumbs>
                            </Stack>
                        </Grid>
                    )}
                    
                    {dossierSchema.map(({label, value, id}) => (
                        <InfoLine 
                            key={id}
                            id={id}
                            label={label}
                            value={value}
                        />
                    ))}

                    {firstObservation && (
                        <InfoLine
                            id='firstObservationDate'
                            label='Date of First Observation'
                            value={firstObservation.get('date')}
                            dateSpecificity={firstObservation.get('dateSpecificity')}
                        />
                    )}
                    
                    {lastObservation && (
                        <InfoLine
                            id='lastObservationDate'
                            label='Date of Most Recent Observation'
                            value={lastObservation.get('date')}
                            dateSpecificity={lastObservation.get('dateSpecificity')}
                        />
                    )}

                    {location.get('events') && location.get('events').length > 0 && (
                        <Grid item xs={12}>
                            <Stack sx={{ mt: 2 }}>
                                <Typography variant='body2'>
                                    Associated Events
                                </Typography>
                                <Card sx={{background: theme.palette.secondary[50]}}>
                                    <Grid container>
                                        {location.get('events').map((event, index) => (
                                            <Grid item xs={12} key={index}>
                                                <Stack direction="row" alignItems="center">
                                                    <Typography variant='body1' sx={{ mr: 1 }}>
                                                        {event.get('name')} ({event.getByPath(['type', 'label'])})
                                                    </Typography>
                                                    <IconButton size="sm" onClick={() => navigate(`/event/${event.id}`)}>
                                                        <ArrowOutwardIcon fontSize="small" />
                                                    </IconButton>
                                                </Stack>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Card>
                            </Stack>
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        {notes && notes.length > 0 && (
                            <Stack>
                                <Typography variant='body2'>
                                    Notes
                                </Typography>
                                <Card sx={{background: theme.palette.primary[50]}}>
                                    <Grid container>
                                        {notes.map((note) => (
                                            <>
                                                <Grid item xs={3}>
                                                    <Typography variant='body2'>
                                                        {note.getByPath(['type', 'label'])}:
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={9}>
                                                    <Typography variant='body1'>
                                                        {note.getByPath(['text'])}
                                                    </Typography>
                                                </Grid>
                                            </>
                                        ))}
                                    </Grid>
                                </Card>
                            </Stack>
                        )}
                    </Grid>

                </Grid>
            </Card>
        </Sheet>
    );
}; 

LocationDossier.propTypes = {
    location: PropTypes.instanceOf(Location).isRequired,
    notes: PropTypes.array,
    firstObservation: PropTypes.object,
    lastObservation: PropTypes.object
};

LocationDossier.defaultProps = {
    notes: [],
    firstObservation: null,
    lastObservation: null
};

export default LocationDossier;